// @flow
import React from 'react'
import moment from 'moment'
import { Button } from 'components/Button'
import Card from 'components/Card/Card'
import styles from './styles.css'
import { fileSubtitle, isViewable } from 'lib/digital_file/viewer'
import type { DigitalFile as DigitalFileType } from 'lib/digital_file/model'

type Props = {
  file: DigitalFileType,
  archived?: boolean,
  busy?: boolean,
  onArchive?: (id: number) => mixed,
  onRestore?: (id: number) => mixed,
  onView?: (key: ?string, contentType: ?string, name: ?string) => mixed,
  onDownload?: (key: ?string) => mixed,
  onMove?: (id: number) => mixed,
  onRename?: (id: number) => mixed,
}

const formatDate = (val: ?string) =>
  val ? moment.utc(val).local().format('MMMM D, YYYY') : null

const FileCard = ({
  file,
  archived,
  busy,
  onArchive,
  onRestore,
  onView,
  onDownload,
  onMove,
  onRename,
}: Props) => {
  const uploaded = formatDate(file.uploaded_at)
  const archivedOn = formatDate(file.archived_at)
  const canRetrieve = Boolean(file.key)
  const canView = canRetrieve && isViewable(file)

  return (
    <Card variant="flat" className={styles.fileCard}>
      <div className={styles.fileCardHeader}>
        <div className={styles.fileCardTitleBlock}>
          <div className={styles.fileCardTitle}>
            {file.name}
            {onRename && (
              <button
                type="button"
                title="Rename file"
                className={styles.renameBtn}
                onClick={() => onRename(file.id)}
              />
            )}
          </div>
          <div className={styles.fileCardSubtitle}>{fileSubtitle(file)}</div>
        </div>
        <div className={styles.fileCardActions}>
          {canView && (
            <Button
              variant="text"
              label="View"
              onClick={() => onView && onView(file.key, file.content_type, file.name)}
              disabled={!onView}
            />
          )}
          <Button
            variant="text"
            label="Download"
            onClick={() => onDownload && onDownload(file.key)}
            disabled={!canRetrieve || !onDownload}
          />
          {archived ? (
            <Button
              variant="text"
              label="Restore"
              onClick={() => onRestore && onRestore(file.id)}
              disabled={busy || !onRestore}
            />
          ) : (
            <>
              <Button
                variant="text"
                label="Move"
                onClick={() => onMove && onMove(file.id)}
                disabled={busy || !onMove}
              />
              <Button
                variant="text"
                label="Archive"
                onClick={() => onArchive && onArchive(file.id)}
                disabled={busy || !onArchive}
              />
            </>
          )}
        </div>
      </div>
      <div className={styles.fileCardInset}>
        {archived && archivedOn ? (
          <div className={styles.metaRow}>
            <strong>Archived:</strong> {archivedOn}
            {file.archived_by ? ` by ${file.archived_by}` : ''}
          </div>
        ) : null}
        {uploaded ? (
          <div className={styles.metaRow}>
            <strong>Uploaded:</strong> {uploaded}
          </div>
        ) : null}
        {file.uploaded_by ? (
          <div className={styles.metaRow}>
            <strong>By:</strong> {file.uploaded_by}
          </div>
        ) : null}
      </div>
    </Card>
  )
}

export default FileCard
